import { useEffect, useState } from "react";
import type { PrintJobStatusView } from "@fiket/booth-schema";
import { fetchPrintJob } from "./printJobApi";
import { PRINT_POLL_INTERVAL_MS, isPollTerminal } from "./printJobView";
import { trackError } from "../../telemetry/activity";

export interface PrintJobPoll {
  /** 마지막으로 받은 상태. 첫 응답 전 null */
  status: PrintJobStatusView | null;
  /** 마지막 폴링 실패 여부(네트워크·404). 화면 판정은 status·시간으로 하고 이 값은 로그용 */
  pollError: boolean;
  /** 훅 시작 시각 — 지연 판정 기준(클라이언트 시계) */
  startedAtMs: number;
  /** printing 을 처음 관측한 시각 — 링 보간 기준 */
  printingSinceMs: number | null;
  /** 500ms 틱 — 보간 렌더 갱신용 */
  nowMs: number;
}

const TICK_MS = 500;

/**
 * GET /api/print-jobs/:id 를 2초 간격으로 폴링(스펙 §6). printed·cancelled 에서 멈추고 failed 는 계속 본다.
 * 이탈 시 AbortController 로 진행 중 fetch·sleep 을 함께 취소 — useTransformJob 과 같은 패턴.
 */
export function usePrintJob(printId: string): PrintJobPoll {
  const [status, setStatus] = useState<PrintJobStatusView | null>(null);
  const [pollError, setPollError] = useState(false);
  const [startedAtMs] = useState(() => Date.now());
  const [printingSinceMs, setPrintingSinceMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      while (!signal.aborted) {
        try {
          const view = await fetchPrintJob(printId, signal);
          setStatus(view);
          setPollError(false);
          if (view.status === "printing") setPrintingSinceMs((prev) => prev ?? Date.now());
          if (isPollTerminal(view)) return;
        } catch (err) {
          if (signal.aborted) return;
          console.warn("[Print] 인쇄 상태 조회 실패 — 다음 폴링에서 재시도", err);
          trackError("print_poll_failed", err);
          setPollError(true);
        }
        try {
          await sleep(PRINT_POLL_INTERVAL_MS, signal);
        } catch {
          return; // abort
        }
      }
    })();
    return () => controller.abort();
  }, [printId]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return { status, pollError, startedAtMs, printingSinceMs, nowMs };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(), ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true }
    );
  });
}
